import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type {
  GenerateQrResponse,
  LogDeliveryRequest,
  LogDeliveryResponse,
  PushAssistListResponse,
  PushAssistUserDetail,
  UploadConsentResponse,
} from "../schemas/pushAssist";

/**
 * UC-009 push-assist admin queries + mutations (Phase 25).
 *
 * - `usePushAssistList` — list users s push_assist_mode=TRUE.
 * - `usePushAssistDetail` — detail (consent badge + history).
 * - `useUploadPaperConsent` — multipart upload + invalidate detail.
 * - `useWithdrawConsent` — append withdrawn consent + invalidate.
 * - `useGenerateQr` — POST /qr (hard block bez consent_pdf) → PNG base64.
 * - `useLogDelivery` — POST /push-assist-delivery.
 */

const KEY_LIST = ["admin", "push-assist", "list"] as const;
const KEY_DETAIL = (userId: string) => ["admin", "push-assist", "detail", userId] as const;

export function usePushAssistList() {
  return useQuery({
    queryKey: KEY_LIST,
    queryFn: () => apiCall<PushAssistListResponse>("/api/admin/push-assist"),
  });
}

export function usePushAssistDetail(userId: string | null) {
  return useQuery({
    queryKey: KEY_DETAIL(userId ?? "none"),
    queryFn: () => apiCall<PushAssistUserDetail>(`/api/admin/push-assist/${userId}`),
    enabled: !!userId,
  });
}

export function useUploadPaperConsent(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("consent_pdf", file);
      return apiCall<UploadConsentResponse>(
        `/api/admin/push-assist/${userId}/paper-consent`,
        { method: "POST", body: form },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
      qc.invalidateQueries({ queryKey: KEY_DETAIL(userId) });
    },
  });
}

export function useWithdrawConsent(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      return apiCall<{ ok: boolean }>(
        `/api/admin/push-assist/${userId}/withdraw-consent`,
        {
          method: "POST",
          body: JSON.stringify({ reason }),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_LIST });
      qc.invalidateQueries({ queryKey: KEY_DETAIL(userId) });
    },
  });
}

export function useGenerateQr(userId: string) {
  return useMutation({
    mutationFn: async (paymentIntentId: string) => {
      return apiCall<GenerateQrResponse>(
        `/api/admin/push-assist/${userId}/qr`,
        {
          method: "POST",
          body: JSON.stringify({ payment_intent_id: paymentIntentId }),
        },
      );
    },
  });
}

export function useLogDelivery(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: LogDeliveryRequest) => {
      return apiCall<LogDeliveryResponse>(
        `/api/admin/users/${userId}/push-assist-delivery`,
        { method: "POST", body: JSON.stringify(req) },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_DETAIL(userId) });
    },
  });
}
